import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { headerSaga } from 'app/pages/Header/saga';
import { reducer, sliceKey } from 'app/pages/Header/slice';
import MessageContentService, {
  MessageContent,
  MessageContentListResponse,
  MessageContentOperation,
} from 'app/apiService/messageContent/messageContentApiService';
import ValueContentService, { ValueContent } from 'app/apiService/valueContent/valueContentApiService';
import DealerSpecialsService, { DealerSpecial } from 'app/apiService/dealerSpecials/dealerSpecialsApiService';
import FooterImageService, { FooterImage } from 'app/apiService/footerImage/footerImageApiService';

export interface LocalOperationState {
  dealer_values: string[];
  dealer_specials: string[];
  footer_image: string[];
}

type LocalOperationsMap = Record<string, LocalOperationState>;

const extractIds = (items: any[]): string[] => {
  if (!items || !Array.isArray(items)) return [];
  return items
    .map(item => {
      if (typeof item === 'string') return item;
      return item._id || item.id || '';
    })
    .filter(Boolean);
};

const buildLocalOperations = (operations: MessageContentOperation[]): LocalOperationsMap => {
  const map: LocalOperationsMap = {};
  operations.forEach(op => {
    map[op.type] = {
      dealer_values: extractIds(op.dealer_values),
      dealer_specials: extractIds(op.dealer_specials),
      footer_image: extractIds(op.footer_image),
    };
  });
  return map;
};

interface UseMessageContentReturn {
  messageContent: MessageContent | null;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  handleInitialize: () => Promise<void>;
  handleRetry: () => void;
  dealerId: string;
  // Available items for DualListMAIQ
  allDealerValues: ValueContent[];
  allDealerSpecials: DealerSpecial[];
  allFooterImages: FooterImage[];
  isLoadingAvailable: boolean;
  // Local operations state (for unsaved changes)
  localOperations: LocalOperationsMap;
  resetKey: number;
  // Global save/cancel
  isDirty: boolean;
  isSavingAll: boolean;
  saveAllError: string | null;
  handleOperationFieldUpdate: (
    operationType: string,
    field: 'dealer_values' | 'dealer_specials' | 'footer_image',
    selectedIds: string[],
  ) => void;
  handleSaveAll: () => Promise<void>;
  handleCancelAll: () => void;
}

const useMessageContent = (): UseMessageContentReturn => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  const selectedDealer = useSelector(selectDealerSelected);
  const dealerId = selectedDealer?.dealer || '';

  const [messageContent, setMessageContent] = useState<MessageContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [allDealerValues, setAllDealerValues] = useState<ValueContent[]>([]);
  const [allDealerSpecials, setAllDealerSpecials] = useState<DealerSpecial[]>([]);
  const [allFooterImages, setAllFooterImages] = useState<FooterImage[]>([]);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState<boolean>(false);

  const [localOperations, setLocalOperations] = useState<LocalOperationsMap>({});
  const [resetKey, setResetKey] = useState<number>(0);
  const [isSavingAll, setIsSavingAll] = useState<boolean>(false);
  const [saveAllError, setSaveAllError] = useState<string | null>(null);

  const fetchMessageContent = useCallback(async () => {
    if (!dealerId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await MessageContentService.getMessageContent(dealerId);
      const responseData: MessageContentListResponse = response?.body;
      if (responseData?.data && responseData.data.length > 0) {
        const content = responseData.data[0];
        setMessageContent(content);
        setLocalOperations(buildLocalOperations(content.operations));
        setResetKey(prev => prev + 1);
      } else {
        setMessageContent(null);
        setLocalOperations({});
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch message content. Please try again.');
      setMessageContent(null);
    } finally {
      setIsLoading(false);
    }
  }, [dealerId]);

  const fetchAvailableItems = useCallback(async () => {
    if (!dealerId) return;
    setIsLoadingAvailable(true);
    try {
      const [dvRes, dsRes, fiRes] = await Promise.allSettled([
        ValueContentService.getAllValueContent({ dealer: dealerId }),
        DealerSpecialsService.getAllDealerSpecials({ dealer: dealerId }),
        FooterImageService.getAllFooterImages({ dealer: dealerId }),
      ]);

      if (dvRes.status === 'fulfilled') {
        setAllDealerValues(dvRes.value?.body?.data || []);
      }
      if (dsRes.status === 'fulfilled') {
        setAllDealerSpecials(dsRes.value?.body?.data || []);
      }
      if (fiRes.status === 'fulfilled') {
        setAllFooterImages(fiRes.value?.body?.data || []);
      }
    } catch {
      // non-critical, available items loading failure is silent
    } finally {
      setIsLoadingAvailable(false);
    }
  }, [dealerId]);

  const handleInitialize = useCallback(async () => {
    if (!dealerId) return;
    setIsInitializing(true);
    setError(null);
    try {
      await MessageContentService.initializeMessageContent(dealerId);
      await fetchMessageContent();
    } catch (err: any) {
      setError(err?.message || 'Failed to initialize message content. Please try again.');
    } finally {
      setIsInitializing(false);
    }
  }, [dealerId, fetchMessageContent]);

  const handleRetry = useCallback(() => {
    fetchMessageContent();
  }, [fetchMessageContent]);

  const handleOperationFieldUpdate = useCallback(
    (
      operationType: string,
      field: 'dealer_values' | 'dealer_specials' | 'footer_image',
      selectedIds: string[],
    ) => {
      setLocalOperations(prev => ({
        ...prev,
        [operationType]: {
          ...prev[operationType],
          [field]: selectedIds,
        },
      }));
    },
    [],
  );

  const handleSaveAll = useCallback(async () => {
    if (!messageContent) return;
    setIsSavingAll(true);
    setSaveAllError(null);
    try {
      const updatedOperations = messageContent.operations.map(op => {
        const local = localOperations[op.type];
        return {
          type: op.type,
          display_name: op.display_name,
          dealer_values: local?.dealer_values || [],
          dealer_specials: local?.dealer_specials || [],
          footer_image: local?.footer_image || [],
        };
      });
      await MessageContentService.updateMessageContent(messageContent._id, updatedOperations);
      await fetchMessageContent();
    } catch (err: any) {
      setSaveAllError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setIsSavingAll(false);
    }
  }, [messageContent, localOperations, fetchMessageContent]);

  const handleCancelAll = useCallback(() => {
    if (!messageContent) return;
    setLocalOperations(buildLocalOperations(messageContent.operations));
    setSaveAllError(null);
    setResetKey(prev => prev + 1);
  }, [messageContent]);

  const isDirty = useMemo(() => {
    if (!messageContent) return false;
    const original = buildLocalOperations(messageContent.operations);
    return messageContent.operations.some(op => {
      const orig = original[op.type];
      const local = localOperations[op.type];
      if (!orig || !local) return false;
      const sortedCompare = (a: string[], b: string[]) =>
        JSON.stringify([...a].sort()) !== JSON.stringify([...b].sort());
      return (
        sortedCompare(orig.dealer_values, local.dealer_values) ||
        sortedCompare(orig.dealer_specials, local.dealer_specials) ||
        sortedCompare(orig.footer_image, local.footer_image)
      );
    });
  }, [messageContent, localOperations]);

  useEffect(() => {
    fetchMessageContent();
  }, [fetchMessageContent]);

  useEffect(() => {
    fetchAvailableItems();
  }, [fetchAvailableItems]);

  return {
    messageContent,
    isLoading,
    isInitializing,
    error,
    handleInitialize,
    handleRetry,
    dealerId,
    allDealerValues,
    allDealerSpecials,
    allFooterImages,
    isLoadingAvailable,
    localOperations,
    resetKey,
    isDirty,
    isSavingAll,
    saveAllError,
    handleOperationFieldUpdate,
    handleSaveAll,
    handleCancelAll,
  };
};

export default useMessageContent;
